import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Check, Building2, Users } from "lucide-react";
import TopNav from "@/components/TopNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatPrice = (cents) => {
  if (!cents || cents === 0) return "Free";
  return `R${(cents / 100).toFixed(2)}`;
};

export default function PricingPage() {
  const [personalPlans, setPersonalPlans] = useState([]);
  const [corpPlans, setCorpPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const [personalRes, corpRes] = await Promise.all([
        axios.get(`${API}/tenant/plans?type=PERSONAL`),
        axios.get(`${API}/tenant/plans?type=CORPORATE`),
      ]);
      setPersonalPlans(personalRes.data.plans || []);
      setCorpPlans(corpRes.data.plans || []);
    } catch (err) {
      console.error("Failed to load plans:", err);
    } finally {
      setLoading(false);
    }
  };

  const PlanCard = ({ plan, index }) => {
    const hubCount = Object.values(plan.hubs || {}).filter(Boolean).length;
    const isFree = plan.price_monthly === 0 && plan.price_yearly === 0;
    const isPersonal = plan.plan_type === "PERSONAL";
    const isPopular = plan.id === "plan_user_monthly" || plan.id === "plan_corp_monthly";

    const getPriceDisplay = () => {
      if (isFree) return "Free";
      if (plan.billing_period === "monthly") return `${formatPrice(plan.price_monthly)}/mo`;
      if (plan.billing_period === "yearly") return `${formatPrice(plan.price_yearly)}/yr`;
      if (plan.billing_period === "per_user") return `${formatPrice(plan.price_per_user_monthly)}/user/mo`;
      return "Custom";
    };

    return (
      <Card className={`relative p-6 flex flex-col ${isPopular ? "ring-2 ring-primary shadow-lg" : ""}`}>
        {isPopular && (
          <Badge className="absolute -top-2 right-4">Most Popular</Badge>
        )}
        <div className="flex items-center gap-2 mb-2">
          {isPersonal ? <Users className="w-5 h-5" /> : <Building2 className="w-5 h-5" />}
          <h3 className="text-lg font-semibold">{plan.name}</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">{plan.description}</p>
        <div className="text-3xl font-bold mb-4">{getPriceDisplay()}</div>
        <div className="space-y-2 mb-6 flex-1">
          <Feature icon={Check} text={`${hubCount} hubs included`} />
          {plan.features?.max_team_members && (
            <Feature icon={Check} text={`Up to ${plan.features.max_team_members} team members`} />
          )}
          <Feature icon={Check} text={`${plan.features?.max_storage_gb || 0}GB storage`} />
          {plan.features?.api_access && <Feature icon={Check} text="API access" />}
          {plan.features?.priority_support && <Feature icon={Check} text="Priority support" />}
          {!plan.features?.api_access && <Feature icon={Check} text="Pay-as-you-go on premium services" />}
        </div>
        <Link to="/">
          <Button className="w-full" variant={isPopular ? "default" : "outline"}>
            {isFree ? "Get Started Free" : "Choose Plan"}
          </Button>
        </Link>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <TopNav />
      <div className="container mx-auto px-4 py-12 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-3">Choose Your Plan</h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Start free and upgrade as you grow. All plans include pay-as-you-go access to premium features.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" /></div>
        ) : (
          <>
            {/* Personal Plans */}
            <section className="mb-16">
              <h2 className="text-2xl font-semibold mb-2">For Individuals</h2>
              <p className="text-muted-foreground mb-6">Perfect for personal use and independent professionals</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {personalPlans.map((plan, i) => (
                  <PlanCard key={plan.id} plan={plan} index={i} />
                ))}
              </div>
            </section>

            {/* Corporate Plans */}
            <section>
              <h2 className="text-2xl font-semibold mb-2">For Organisations</h2>
              <p className="text-muted-foreground mb-6">
                Empower your team with corporate tools, custom hub selection, and dedicated support
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {corpPlans.map((plan, i) => (
                  <PlanCard key={plan.id} plan={plan} index={i} />
                ))}
              </div>
            </section>
          </>
        )}

        {/* Bottom CTA */}
        <div className="text-center mt-16 p-8 rounded-xl bg-muted/50">
          <h2 className="text-2xl font-bold mb-2">Need a custom plan?</h2>
          <p className="text-muted-foreground mb-4">
            Contact us for enterprise pricing with custom hub selection, dedicated infrastructure, and SLA.
          </p>
          <Button variant="outline" asChild>
            <a href="mailto:enterprise@allbaise.com">Contact Sales</a>
          </Button>
        </div>
      </div>
    </div>
  );
}

function Feature({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <Icon className="w-4 h-4 text-green-500 flex-shrink-0" />
      <span>{text}</span>
    </div>
  );
}
